import { useEffect, useState } from "react";

/**
 * Second-resolution countdown from a UTC ISO timestamp to zero.
 * Returns remaining seconds (0 when expired). Cleans up on unmount.
 */
export function useCountdown(expiresAt: string): number {
  const [secs, setSecs] = useState(() => secondsUntil(expiresAt));

  useEffect(() => {
    // Re-sync from server timestamp whenever expiresAt changes
    const initial = secondsUntil(expiresAt);
    setSecs(initial);
    if (initial <= 0) return;

    const id = setInterval(() => {
      setSecs((prev) => {
        const next = prev - 1;
        if (next <= 0) {
          clearInterval(id);
          return 0;
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  return secs;
}

function secondsUntil(iso: string): number {
  const ms = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.floor(ms / 1000));
}

/** Format seconds into MM:SS or HH:MM:SS */
export function formatCountdown(secs: number): string {
  if (secs <= 0) return "00:00";
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  const mm = String(m).padStart(2, "0");
  const ss = String(s).padStart(2, "0");
  if (h > 0) {
    return `${String(h).padStart(2, "0")}:${mm}:${ss}`;
  }
  return `${mm}:${ss}`;
}
